<!-- ProjectSearch.vue -->
<template>
  <div class="space-y-4 p-4">
    <!-- Search Input -->
    <div class="relative">
      <input v-model="searchQuery" type="text"
        class="w-full h-[44px] px-3 pl-10 py-2 rounded-md bg-[#F6F8F9] focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        placeholder="Search projects by name, status, or description" @input="debounceSearch" />
      <button
        class="absolute top-1/2 left-3 transform -translate-y-1/2 h-5 w-5 flex items-center justify-center cursor-pointer"
        aria-label="Search">
        <img src="@/assets/projects/search-normal.svg" alt="Search Projects" class="h-4 w-4" />
      </button>
      <div v-if="searchQuery && searchQuery.length > 0" @click="clearSearch"
        class="absolute top-1/2 right-3 transform -translate-y-1/2 h-5 w-5 flex items-center justify-center cursor-pointer">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6L18 18" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      </div>
    </div>

    <!-- Loading Indicator -->
    <div v-if="isSearching" class="flex justify-center py-4">
      <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
    </div>

    <!-- Search Results -->
    <div v-if="!isSearching && searchQuery.length > 0" class="space-y-4">
      <div v-if="filteredProjects.length === 0" class="text-center py-8">
        <p class="text-gray-500">No projects found matching "{{ searchQuery }}"</p>
      </div>

      <div v-else class="space-y-2">
        <h3 class="font-medium text-gray-700">Found ({{ filteredProjects.length }}) projects</h3>

        <div class="space-y-2 max-h-[400px] overflow-y-auto">
          <div v-for="project in filteredProjects" :key="project.id" @click="goToProject(project.project_id)"
            class="flex items-center justify-between p-3 bg-white hover:bg-primaryBg cursor-pointer">
            <div class="flex items-center space-x-3">
              <img src="@/assets/dashboard/team-img2.svg" alt="team" class="h-6 overlap" />

              <div class="flex justify-between">
                <h4 class="text-[14px]">{{ project.project_name }}</h4>
                <div class="w-2 h-2 rounded-full" :class="{
                  'bg-green-500': project.status === 'Active',
                  'bg-gray-500': project.status === 'On Hold',
                  'bg-blue-500': project.status === 'Completed'
                }"></div>
                <!-- <p class="text-xs text-gray-500">{{ project.status }}</p> -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Project Button (if admin or manager) -->
    <!-- <div v-if="(isAdmin || isManager) && searchQuery.length > 0 && !filteredProjects.length" class="mt-4">
      <button 
        @click="createProject"
        class="w-full flex justify-center items-center space-x-2 bg-black text-white py-2 px-4 rounded-md hover:bg-gray-800"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 5V19M5 12H19" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Create New Project</span>
      </button>
    </div> -->

    <!-- Recent Projects (when no search) -->
    <!-- <div v-if="!isSearching && searchQuery.length === 0" class="space-y-4">
      <h3 class="font-medium text-gray-700">Recent Projects</h3>
      <div v-if="recentProjects.length > 0" class="space-y-2 max-h-[400px] overflow-y-auto">
        <div 
          v-for="project in recentProjects" 
          :key="project.id"
          @click="goToProject(project.project_id)"
          class="flex items-center justify-between p-3 bg-white hover:bg-gray-50 rounded-md border border-gray-200 cursor-pointer"
        >
          <div class="flex items-center space-x-3">
            <div class="w-2 h-2 rounded-full" 
                 :class="{
                    'bg-green-500': project.status === 'Active',
                    'bg-gray-500': project.status === 'On Hold',
                    'bg-blue-500': project.status === 'Completed'
                 }"></div>
            <div>
              <h4 class="font-medium text-sm">{{ project.project_name }}</h4>
              <p class="text-xs text-gray-500">{{ project.status }}</p>
            </div>
          </div>
          <div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-8">
        <p class="text-gray-500">No recent projects found</p>
      </div>
    </div> -->
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'ProjectSearch',
  props: {
    projects: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    isManager: {
      type: Boolean,
      default: false
    },
    isTalent: {
      type: Boolean,
      default: false
    },
    isInvestor: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: '',
      isSearching: false,
      filteredProjects: [],
    };
  },
  computed: {
    recentProjects() {
      return this.projects;
    }
  },
  methods: {
    debounceSearch: _.debounce(function () {
      this.searchProjects();
    }, 300),

    searchProjects() {
      this.isSearching = true;

      if (!this.searchQuery || this.searchQuery.trim() === '') {
        this.filteredProjects = [];
        this.isSearching = false;
        return;
      }

      const searchTerm = this.searchQuery.toLowerCase();

      // Filter projects based on name, description, and status
      this.filteredProjects = this.projects.filter(project => {
        const matchName = project.project_name && project.project_name.toLowerCase().includes(searchTerm);
        const matchDescription = project.description && project.description.toLowerCase().includes(searchTerm);
        const matchStatus = project.status && project.status.toLowerCase().includes(searchTerm);

        return matchName || matchDescription || matchStatus;
      });

      this.isSearching = false;
    },
    goToProject(projectId) {
      this.$router.push({ name: 'ProjectDetails', params: { projectId } });
    },

    createProject() {
      this.$emit('create-project');
    },

    clearSearch() {
      this.searchQuery = '';
      this.filteredProjects = [];
    },

    resetSearch() {
      this.searchQuery = '';
      this.filteredProjects = [];
    }
  }
};
</script>